import type { MenuCellData } from './types';

import {
  isCustomCellData,
  getCustomCellTemplate,
  isKindOfCustomCell,
} from '../utils';

import { kind } from './types';

const allowedValues = [
  null,
  'b',
  's',
  'buy',
  'sell',
  {
    value: 'buy',
    label: 'Buy',
  },
  {
    value: 'sell',
    label: 'Sell',
  },
] as const;

const isShorthandBuy = (value: unknown): value is 'b' => {
  if (typeof value !== 'string') return false;
  return value.toLowerCase() === 'b';
};

const isShorthandSell = (value: unknown): value is 's' => {
  if (typeof value !== 'string') return false;
  return value.toLowerCase() === 's';
};

const getDefaultCellData = ({
  value = null,
  error = null,
}: Partial<MenuCellData> = {}): MenuCellData => ({
  kind,
  allowedValues,
  value,
  error,
});

const isCellData = (data: unknown): data is MenuCellData => {
  if (!isCustomCellData(data)) return false;
  return 'kind' in data && data.kind === kind;
};

export const getAllowedValue = (
  allowedValues: MenuCellData['allowedValues'],
  value: string | null
) => {
  if (value === null) return '';
  const option = allowedValues.find((n) => {
    if (typeof n === 'string' || n === null) {
      return n === value;
    }
    return n.value === value;
  });

  if (typeof option === 'undefined' || option === null) return '';
  if (typeof option === 'string') return option;
  return option.value;
};

type Args = Pick<Partial<MenuCellData>, 'error'> & { value?: unknown };

/**
 * ## getMenuCellTemplate
 *
 * @param value The value of the MenuCell
 * @returns A custom MenuCell object and its data
 */
export const getCellTemplate = ({ value = null, error = null }: Args = {}) => {
  if (typeof value === 'string') {
    const copyData = value ?? '';
    const cellData = getDefaultCellData({ value, error });
    return getCustomCellTemplate({ cellData, copyData });
  }
  return getCustomCellTemplate({ cellData: getDefaultCellData() });
};

export const isCell = (
  cell: unknown
): cell is ReturnType<typeof getCellTemplate> => {
  if (typeof cell !== 'object') return false;
  if (cell === null) return false;
  if (isKindOfCustomCell(cell)) {
    return isCellData(cell.data);
  }
  return false;
};

export const getCellData = (data?: unknown) => {
  if (isCellData(data)) {
    return data;
  }
  return getDefaultCellData();
};

export const validatePastedValue = (
  pastedValue: string,
  cell: MenuCellData
): MenuCellData => {
  const sanitisedValue = pastedValue.trim().toLowerCase();

  if (cell.allowedValues.includes(sanitisedValue) === false) {
    return { ...cell, error: 'Enter “Buy” or “Sell”' };
  }

  if (isShorthandBuy(sanitisedValue)) {
    return {
      ...cell,
      value: 'buy',
      error: null,
    };
  }

  if (isShorthandSell(sanitisedValue)) {
    return {
      ...cell,
      value: 'sell',
      error: null,
    };
  }

  return {
    ...cell,
    value: sanitisedValue,
    error: null,
  };
};
